import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from "recharts";

const PIE_COLORS = ["#FF8800", "#00B0FF", "#00E676", "#F43F5E", "#A78BFA", "#FACC15", "#22D3EE"];
const STATUS_LABEL = { payment_pending: "Payment Pending", paid: "Paid", assigned: "Assigned", in_progress: "In Progress", completed: "Completed" };

const tooltipStyle = { background: "#0D111D", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 10, fontSize: 12, color: "#e2e8f0" };

function Panel({ title, children, testid }) {
  return (
    <div className="glass rounded-xl p-5" data-testid={testid}>
      <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#00B0FF] uppercase mb-4">{title}</p>
      {children}
    </div>
  );
}

export function PortalAnalyticsModule() {
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/portal/admin/analytics").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => { load(); }, [load]);

  const cleanTestData = async () => {
    if (!window.confirm("Delete all throwaway test founder accounts (testfounder_*, teamtest_*, locktest*, etc.) and their data?")) return;
    try {
      const r = await api.post("/portal/admin/cleanup-test-data");
      toast.success(`Removed ${r.data.founders_removed} test founders, ${r.data.startups_removed ?? 0} startups, ${r.data.orders_removed ?? 0} orders`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!data) {
    return <div className="flex justify-center py-16"><div className="w-8 h-8 rounded-full border-2 border-[#FF8800] border-t-transparent animate-spin" /></div>;
  }

  const statusData = Object.entries(data.orders_by_status || {}).map(([k, v]) => ({ name: STATUS_LABEL[k] || k, value: v }));
  const maxFunnel = Math.max(1, ...(data.funnel || []).map((f) => f.value));

  const stats = [
    { label: "Revenue", value: `₹${(data.revenue || 0).toLocaleString("en-IN")}` },
    { label: "Founders", value: data.founders },
    { label: "Startups", value: data.total_startups },
    { label: "Orders", value: data.total_orders },
    { label: "Avg AI Score", value: data.avg_score },
    { label: "Pending Docs", value: data.pending_documents },
  ];

  return (
    <div data-testid="admin-analytics-module" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-white">Portal Analytics</h2>
          <p className="text-sm text-slate-400">Startup OS performance — revenue, funnel, categories and specialists.</p>
        </div>
        <div className="flex items-center gap-4">
          <button onClick={cleanTestData} data-testid="analytics-clean-test-data" className="text-xs text-slate-400 hover:text-red-400 transition-colors">Clean Test Data</button>
          <button onClick={load} data-testid="analytics-refresh" className="text-xs text-slate-400 hover:text-[#FF8800] transition-colors">Refresh</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="analytics-stats">
        {stats.map((s) => (
          <div key={s.label} className="glass rounded-xl p-4" data-testid={`analytics-stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="font-display font-extrabold text-xl text-white">{s.value}</p>
            <p className="text-[10px] font-mono-tech tracking-wider text-slate-500 uppercase mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Panel title="Revenue by month" testid="analytics-revenue-chart">
          {(data.revenue_by_month || []).length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-10">No payments recorded yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={data.revenue_by_month} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF8800" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#FF8800" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="month" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`₹${Number(v).toLocaleString("en-IN")}`, "Revenue"]} />
                <Area type="monotone" dataKey="amount" stroke="#FF8800" strokeWidth={2} fill="url(#revGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel title="Orders by status" testid="analytics-status-chart">
          {statusData.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-10">No orders yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {statusData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex flex-wrap gap-3 justify-center mt-1">
            {statusData.map((s, i) => (
              <span key={s.name} className="flex items-center gap-1.5 text-[11px] text-slate-400">
                <span className="w-2 h-2 rounded-full" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} /> {s.name} ({s.value})
              </span>
            ))}
          </div>
        </Panel>

        <Panel title="Orders by service category" testid="analytics-category-chart">
          {(data.orders_by_category || []).length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-10">No orders yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.orders_by_category} layout="vertical" margin={{ top: 5, right: 16, left: 10, bottom: 0 }}>
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="category" width={140} tick={{ fill: "#94a3b8", fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" fill="#00B0FF" radius={[0, 6, 6, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>

        <Panel title="Conversion funnel" testid="analytics-funnel">
          <div className="space-y-2.5">
            {(data.funnel || []).map((f, i) => (
              <div key={f.stage} data-testid={`funnel-stage-${i}`}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-slate-400">{f.stage}</span>
                  <span className="text-white font-mono-tech">{f.value}</span>
                </div>
                <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#FF8800] to-[#00E676]" style={{ width: `${(f.value / maxFunnel) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel title="Service → signup conversion" testid="analytics-attribution">
        {(data.service_attribution || []).length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">No attributed signups yet. They appear when visitors register via a service CTA.</p>
        ) : (
          <div className="space-y-2">
            {data.service_attribution.map((row, i) => (
              <div key={row.service_id} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 px-4 py-2.5" data-testid={`attribution-row-${i}`}>
                <p className="text-sm font-medium text-white truncate">{row.service_name}</p>
                <div className="flex items-center gap-4 shrink-0 text-xs font-mono-tech">
                  <span className="text-[#00B0FF]">{row.signups} signup{row.signups === 1 ? "" : "s"}</span>
                  <span className="text-[#00E676]">{row.paid} paid</span>
                  <span className="text-slate-400">{row.signups ? Math.round((row.paid / row.signups) * 100) : 0}% conv.</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {(data.utm_sources || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/10" data-testid="utm-sources">
            {data.utm_sources.map((u) => (
              <span key={u.source} className="text-[11px] font-mono-tech px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-slate-400">
                {u.source}: {u.signups}
              </span>
            ))}
          </div>
        )}
        {(data.referral_leaderboard || []).length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10" data-testid="referral-leaderboard">
            <p className="font-mono-tech text-[10px] tracking-[0.25em] text-[#FF8800] uppercase mb-2">Founder referrals</p>
            {data.referral_leaderboard.map((r, i) => (
              <div key={i} className="flex justify-between text-xs py-1" data-testid={`referral-row-${i}`}>
                <span className="text-slate-300">{r.name}</span>
                <span className="font-mono-tech text-slate-400">{r.signups} joined · {r.paid} paid</span>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Specialist workload" testid="analytics-specialists">
        {(data.specialist_stats || []).length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-6">No specialists yet.</p>
        ) : (
          <div className="space-y-2">
            {data.specialist_stats.map((sp, i) => (
              <div key={i} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 px-4 py-2.5" data-testid={`analytics-specialist-${i}`}>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-white truncate">{sp.name}</p>
                  <p className="text-[11px] text-slate-500">{sp.category}</p>
                </div>
                <div className="flex items-center gap-4 shrink-0 text-xs font-mono-tech">
                  <span className="text-[#00B0FF]">{sp.active} active</span>
                  <span className="text-[#00E676]">{sp.completed} done</span>
                  <span className={sp.is_active ? "text-[#00E676]" : "text-red-400"}>{sp.is_active ? "ACTIVE" : "SUSPENDED"}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}
